import { PizzaComplementsGroupIds } from '../enum/Pizza';
import ICart from '../models/ICart';
import { IGroupChangedData } from '../models/ICartItem';
import ICartItemAnalytics, { IProductListContext } from '../models/ICartItemAnalytics';
import ICompany from '../models/ICompany';
import IProduct from '../models/IProduct';
import {
  allocateDiscount,
  fromCents,
  getCartLineTotal,
  splitLineIntoEntries,
  toCents,
} from '../utils/cart-price';
import { getProductDisplayDiscount, getProductDisplayPrice } from '../utils/product-price';
import {
  dispatchGa4Ecommerce,
  Ga4EcommerceEventName,
  IGa4EcommercePayload,
  IGa4EmissionResult,
  IGa4Item,
  ITrackingDestinations,
} from './ga4';

/** GA4 accepts at most 200 entries in the items array of a single event. */
export const GA4_MAX_ITEMS = 200;

/** Entry that stands for everything dropped past the cap, so `value` survives untouched. */
export const OVERFLOW_ITEM_ID = 'bsfood:overflow';

const MAX_FOOD_VALUES = 10;
const MAX_FOOD_VALUE_LENGTH = 40;
const MAX_FOOD_PARAM_LENGTH = 100;

const CURRENCY = 'BRL';

export type Ga4ShippingTier = 'delivery' | 'pickup' | 'drive_thru' | 'dine_in';

const SHIPPING_TIERS: Record<string, Ga4ShippingTier> = {
  DELIVERY: 'delivery',
  CHECKOUT: 'pickup',
  DRIVE_THRU: 'drive_thru',
  ON_TABLE: 'dine_in',
};

export const toShippingTier = (deliveryType?: string): Ga4ShippingTier | undefined =>
  SHIPPING_TIERS[deliveryType || ''];

/**
 * Payment types already come as SCREAMING_SNAKE, which is the GA4 convention lower cased.
 *
 * `NONE` is the exception: it is what pickup and drive-thru get, and it means the order carries no
 * payment at all. Reporting it would invent a payment method and a funnel step the visitor never
 * took. If paying at the venue ever needs to be measured, it has to become a real value in the
 * catalog of payment types, not something inferred here.
 */
export const toPaymentType = (paymentType?: string): string | undefined =>
  paymentType && paymentType !== 'NONE' ? paymentType.toLowerCase() : undefined;

const destinationsOf = (company: ICompany): ITrackingDestinations => ({
  ga4Id: company?.ga4Id,
  gtmId: company?.gtmId,
});

/**
 * Food parameters are free text coming from the catalog, so they are trimmed, truncated, ordered
 * alphabetically (click order would make the same item report different values) and capped in
 * count. Customer written text never reaches here: `comments` is not read anywhere in this module.
 */
const normalizeFoodValues = (titles: (string | undefined)[]): string | undefined => {
  const values = titles
    .map((title) => (title || '').replace(/\s+/g, ' ').trim().slice(0, MAX_FOOD_VALUE_LENGTH))
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right, 'pt-BR'))
    .slice(0, MAX_FOOD_VALUES);

  if (!values.length) return undefined;

  return values.join(', ').slice(0, MAX_FOOD_PARAM_LENGTH);
};

const titlesOfGroup = (groups: IGroupChangedData[], groupId: number): (string | undefined)[] =>
  groups
    .filter((group) => group.id === groupId)
    .flatMap((group) => group.complements.map((complement) => complement.title));

const PIZZA_GROUP_IDS = [
  PizzaComplementsGroupIds.FLAVORS,
  PizzaComplementsGroupIds.CRUSTS,
  PizzaComplementsGroupIds.EDGES,
];

const addOnTitles = (groups: IGroupChangedData[]): (string | undefined)[] =>
  groups
    .filter((group) => !PIZZA_GROUP_IDS.includes(group.id))
    .flatMap((group) => group.complements.map((complement) => complement.title));

interface IFoodParams {
  food_flavors?: string;
  food_crust?: string;
  food_edge?: string;
  food_add_ons?: string;
}

const buildFoodParams = (item: ICart): IFoodParams => {
  const groups = item.complementsGroups || [];

  return {
    food_flavors:
      item.analytics?.foodFlavors ??
      normalizeFoodValues(titlesOfGroup(groups, PizzaComplementsGroupIds.FLAVORS)),
    food_crust:
      item.analytics?.foodCrust ??
      normalizeFoodValues(titlesOfGroup(groups, PizzaComplementsGroupIds.CRUSTS)),
    food_edge:
      item.analytics?.foodEdge ??
      normalizeFoodValues(titlesOfGroup(groups, PizzaComplementsGroupIds.EDGES)),
    food_add_ons: item.analytics?.foodAddOns ?? normalizeFoodValues(addOnTitles(groups)),
  };
};

/**
 * Stable id for the item. A custom pizza has no product behind it (`productId` is 0), so it is
 * identified by its configuration and never by the cart uuid, which changes on every add.
 */
export const buildCartItemId = (item: ICart): string => {
  if (item.analytics?.itemId) return item.analytics.itemId;
  if (item.sellerSku && item.sellerSku.trim()) return item.sellerSku.trim();
  if (item.isCustomPizza) return `pizza:${item.pizzaCategoryId || 0}:${item.pizzaSizeId || 0}`;

  return `product:${item.productId}`;
};

/**
 * List an item should keep when it is written to the cart again.
 *
 * Editing an item reopens the detail with no list behind it, so without the fallback the original
 * attribution would be erased and the purchase would report the item as coming from nowhere.
 */
export const resolveItemListContext = (
  list?: IProductListContext,
  previous?: ICartItemAnalytics,
): IProductListContext | undefined => list || previous?.list;

const buildVariant = (item: ICart): string | undefined => {
  if (item.analytics?.variant) return item.analytics.variant;

  const parts = [item.productPizzaSize?.name, item.isHalfPrice ? 'meia' : undefined];
  const variant = parts.filter(Boolean).join(' ');

  return variant || undefined;
};

const buildBaseItem = (item: ICart, company: ICompany): Omit<IGa4Item, 'price' | 'quantity'> => ({
  item_id: buildCartItemId(item),
  item_name: item.analytics?.itemName || item.title,
  affiliation: company?.tradingName,
  item_brand: company?.tradingName,
  item_category: item.analytics?.categoryName,
  item_category2: item.analytics?.subcategoryName,
  item_variant: buildVariant(item),
  item_list_id: item.analytics?.list?.listId,
  item_list_name: item.analytics?.list?.listName,
  index: item.analytics?.list?.index,
  ...buildFoodParams(item),
});

const sumItemsCents = (items: IGa4Item[]): number =>
  items.reduce((sum, item) => sum + toCents(item.price) * item.quantity, 0);

const sumOf = (items: IGa4Item[]): number => fromCents(sumItemsCents(items));

const consolidationKey = ({ quantity, ...rest }: IGa4Item): string => JSON.stringify(rest);

/** Merges entries that are identical in everything but quantity. */
const consolidate = (items: IGa4Item[]): IGa4Item[] => {
  const merged = new Map<string, IGa4Item>();

  items.forEach((item) => {
    const key = consolidationKey(item);
    const existing = merged.get(key);

    if (existing) {
      existing.quantity += item.quantity;
      return;
    }

    merged.set(key, { ...item });
  });

  return Array.from(merged.values());
};

/**
 * Keeps the items array within the GA4 cap.
 *
 * Splitting a purchase into two events is not an option — it would break `transaction_id`, revenue
 * and the transaction count — so identical entries are merged first and, if that is still not
 * enough, the tail collapses into a single aggregate that preserves the total.
 */
export const applyItemCap = (items: IGa4Item[]): IGa4Item[] => {
  if (items.length <= GA4_MAX_ITEMS) return items;

  const consolidated = consolidate(items);

  if (consolidated.length <= GA4_MAX_ITEMS) return consolidated;

  const kept = consolidated.slice(0, GA4_MAX_ITEMS - 1);
  const dropped = consolidated.slice(GA4_MAX_ITEMS - 1);

  return [
    ...kept,
    {
      item_id: OVERFLOW_ITEM_ID,
      item_name: 'Outros itens',
      price: fromCents(sumItemsCents(dropped)),
      quantity: 1,
      overflow_item_count: dropped.reduce((sum, item) => sum + item.quantity, 0),
    },
  ];
};

export interface ICartMappingOptions {
  /** Cart wide discount in reais, spread across the lines before the items are built. */
  discountTotal?: number;
  coupon?: string;
}

/**
 * Maps the whole cart, allocating the discount and splitting every line that no single unit price
 * can reproduce. The result always satisfies `value === sum(price * quantity)`.
 */
export const mapCartToItems = (
  cart: ICart[],
  company: ICompany,
  { discountTotal = 0, coupon }: ICartMappingOptions = {},
): IGa4Item[] => {
  const grossCents = cart.map((item) => toCents(getCartLineTotal(item)));
  const discounts = allocateDiscount(grossCents, toCents(discountTotal));

  const items = cart.flatMap((item, index) => {
    const base = buildBaseItem(item, company);

    return splitLineIntoEntries(grossCents[index], discounts[index], item.qty).map((entry) => ({
      ...base,
      coupon,
      price: fromCents(entry.priceCents),
      discount: entry.discountCents ? fromCents(entry.discountCents) : undefined,
      quantity: entry.quantity,
    }));
  });

  return applyItemCap(items);
};

/**
 * Maps a single cart line for a quantity that just moved. No cart wide discount applies here: the
 * event reports the unit price as configured, for the quantity added or removed.
 */
export const mapCartItemDelta = (item: ICart, company: ICompany, quantity: number): IGa4Item[] => {
  if (quantity <= 0 || item.qty <= 0) return [];

  const unitCents = Math.round(toCents(getCartLineTotal(item)) / item.qty);

  return [{ ...buildBaseItem(item, company), price: fromCents(unitCents), quantity }];
};

const buildProductItemId = (product: IProduct): string =>
  product.sellerSku && product.sellerSku.trim()
    ? product.sellerSku.trim()
    : `product:${product.id}`;

export const mapProductToItem = (
  product: IProduct,
  company: ICompany,
  list?: IProductListContext,
): IGa4Item => ({
  item_id: buildProductItemId(product),
  item_name: product.title,
  affiliation: company?.tradingName,
  item_brand: company?.tradingName,
  item_category: product.categories?.[0]?.name,
  item_category2: product.subcategory?.name,
  price: getProductDisplayPrice(product),
  discount: getProductDisplayDiscount(product),
  quantity: 1,
  item_list_id: list?.listId,
  item_list_name: list?.listName,
  index: list?.index,
});

const emit = (
  name: Ga4EcommerceEventName,
  payload: IGa4EcommercePayload,
  company: ICompany,
): IGa4EmissionResult => dispatchGa4Ecommerce(name, payload, destinationsOf(company));

export interface IViewItemListOptions {
  listId: string;
  listName: string;
  products: IProduct[];
  /** Set only when the list is larger than the cap and had to be reported in batches. */
  batchIndex?: number;
  batchCount?: number;
  /** Position of the first product within the whole list, not within the batch. */
  startIndex?: number;
}

export const trackViewItemList = (
  company: ICompany,
  { listId, listName, products, batchIndex, batchCount, startIndex = 0 }: IViewItemListOptions,
): IGa4EmissionResult =>
  emit(
    'view_item_list',
    {
      item_list_id: listId,
      item_list_name: listName,
      batch_index: batchIndex,
      batch_count: batchCount,
      items: products.map((product, position) =>
        mapProductToItem(product, company, {
          listId,
          listName,
          index: startIndex + position,
        }),
      ),
    },
    company,
  );

export const trackSelectItem = (
  company: ICompany,
  product: IProduct,
  list?: IProductListContext,
): IGa4EmissionResult =>
  emit(
    'select_item',
    {
      item_list_id: list?.listId,
      item_list_name: list?.listName,
      items: [mapProductToItem(product, company, list)],
    },
    company,
  );

export const trackViewItem = (
  company: ICompany,
  product: IProduct,
  list?: IProductListContext,
): IGa4EmissionResult =>
  emit(
    'view_item',
    {
      currency: CURRENCY,
      value: getProductDisplayPrice(product),
      items: [mapProductToItem(product, company, list)],
    },
    company,
  );

export const trackAddToCart = (
  company: ICompany,
  item: ICart,
  quantity: number,
): IGa4EmissionResult => {
  const items = mapCartItemDelta(item, company, quantity);

  return emit('add_to_cart', { currency: CURRENCY, value: sumOf(items), items }, company);
};

export const trackRemoveFromCart = (
  company: ICompany,
  item: ICart,
  quantity: number,
): IGa4EmissionResult => {
  const items = mapCartItemDelta(item, company, quantity);

  return emit('remove_from_cart', { currency: CURRENCY, value: sumOf(items), items }, company);
};

export const trackAddMultipleToCart = (company: ICompany, items: ICart[]): IGa4EmissionResult => {
  const mapped = items.flatMap((item) => mapCartItemDelta(item, company, item.qty));

  return emit('add_to_cart', { currency: CURRENCY, value: sumOf(mapped), items: mapped }, company);
};

/**
 * Two cart lines describe the same thing when everything the reports can see matches, unit price
 * included. Quantity is deliberately out: it is what tells a plain quantity change apart from a
 * reconfiguration.
 */
export const isSameCartConfiguration = (left: ICart, right: ICart, company: ICompany): boolean => {
  if (left.qty <= 0 || right.qty <= 0) return false;

  const leftUnit = Math.round(toCents(getCartLineTotal(left)) / left.qty);
  const rightUnit = Math.round(toCents(getCartLineTotal(right)) / right.qty);

  if (leftUnit !== rightUnit) return false;

  return (
    JSON.stringify(buildBaseItem(left, company)) === JSON.stringify(buildBaseItem(right, company))
  );
};

/**
 * Reports a cart line being replaced.
 *
 * A quantity change is a delta on the same item. Anything else — size, variant, flavors, add-ons —
 * is a different item, and reporting it as a delta would credit the new revenue to the old
 * configuration, so the previous one is removed and the new one added.
 */
export const trackCartItemReplacement = (
  company: ICompany,
  previous: ICart,
  next: ICart,
): IGa4EmissionResult[] => {
  if (isSameCartConfiguration(previous, next, company)) {
    const delta = next.qty - previous.qty;

    if (delta === 0) return [];
    if (delta > 0) return [trackAddToCart(company, next, delta)];

    return [trackRemoveFromCart(company, previous, -delta)];
  }

  return [
    trackRemoveFromCart(company, previous, previous.qty),
    trackAddToCart(company, next, next.qty),
  ];
};

export interface ICheckoutStepOptions extends ICartMappingOptions {
  shippingTier?: Ga4ShippingTier;
  paymentType?: string;
}

const cartStepPayload = (
  cart: ICart[],
  company: ICompany,
  options: ICheckoutStepOptions,
): IGa4EcommercePayload => {
  const items = mapCartToItems(cart, company, options);

  return {
    currency: CURRENCY,
    value: sumOf(items),
    coupon: options.coupon,
    items,
  };
};

export const trackViewCart = (
  company: ICompany,
  cart: ICart[],
  options: ICheckoutStepOptions = {},
): IGa4EmissionResult => emit('view_cart', cartStepPayload(cart, company, options), company);

export const trackBeginCheckout = (
  company: ICompany,
  cart: ICart[],
  options: ICheckoutStepOptions = {},
): IGa4EmissionResult => emit('begin_checkout', cartStepPayload(cart, company, options), company);

export const trackAddShippingInfo = (
  company: ICompany,
  cart: ICart[],
  options: ICheckoutStepOptions = {},
): IGa4EmissionResult =>
  emit(
    'add_shipping_info',
    { ...cartStepPayload(cart, company, options), shipping_tier: options.shippingTier },
    company,
  );

export const trackAddPaymentInfo = (
  company: ICompany,
  cart: ICart[],
  options: ICheckoutStepOptions = {},
): IGa4EmissionResult =>
  emit(
    'add_payment_info',
    {
      ...cartStepPayload(cart, company, options),
      payment_type: toPaymentType(options.paymentType),
    },
    company,
  );

export interface IPurchaseOptions extends ICartMappingOptions {
  transactionId: string;
  /** Delivery fee actually charged. A free shipping coupon reports 0, never the original fee. */
  shipping?: number;
  shippingTier?: Ga4ShippingTier;
  paymentType?: string;
}

export const trackPurchase = (
  company: ICompany,
  cart: ICart[],
  { transactionId, shipping, shippingTier, paymentType, ...mapping }: IPurchaseOptions,
): IGa4EmissionResult => {
  const items = mapCartToItems(cart, company, mapping);

  return emit(
    'purchase',
    {
      transaction_id: transactionId,
      currency: CURRENCY,
      value: sumOf(items),
      shipping,
      coupon: mapping.coupon,
      shipping_tier: shippingTier,
      payment_type: toPaymentType(paymentType),
      items,
    },
    company,
  );
};
